import { Genre } from "@filmweb/sanity-types";
import { gql } from "graphql-request";
import { GetServerSideProps } from "next";
import { groq } from "next-sanity";
import { Router } from "next/router";
import { ReactElement } from "react";
import ProgramDisplay from "src/components/film/program/ProgramDisplay";
import FilmFacts from "src/components/filmfacts/FilmFacts";
import ExtraProgramControls from "src/components/premiumlandingpage/ExtraProgramControls";
import Hero from "src/components/premiumlandingpage/Hero";
import Logo from "src/components/premiumlandingpage/Logo";
import NFKFooter from "src/components/premiumlandingpage/NFKFooter";
import NFKHeader from "src/components/premiumlandingpage/NFKHeader";
import NFKLocationSelector from "src/components/premiumlandingpage/NFKLocationSelector";
import PromotedVideo from "src/components/premiumlandingpage/PromotedVideo";
import Ratings from "src/components/premiumlandingpage/Ratings";
import Ingress from "src/components/richtext/Ingress";
import MainText from "src/components/richtext/MainText";
import PremiumLandingPageSeo from "src/components/seo/PremiumLandingPageSeo";
import theme from "src/components/themes/DefaultTheme";
import { LocationWrapper } from "src/lib/contexts/LocationContext";
import ProgramDatesWrapper from "src/lib/contexts/ProgramDatesWrapper";
import SelectedCinemaWrapper from "src/lib/contexts/SelectedCinemaContext";
import { createRichTextQueryFragment } from "src/lib/groq";
import DefaultLayout from "src/lib/pages/DefaultLayout";
import Filmomtale, { getServerSideProps as filmomtaleGetServerSideProps, type FilmomtaleProps } from "src/lib/pages/Filmomtale";
import { setDefaultCacheHeaders } from "src/lib/server/cache";
import { filminfoQuery } from "src/lib/server/filminfo";
import { getServerData, ICommonProps, IQueryResult, isPreview, sanityQuery } from "src/lib/server/sanity";
import styled, { createGlobalStyle, css } from "src/lib/styles/css";
import IPremiumLandingPage, { DocType_PremiumLandingPage } from "src/lib/types/IPremiumLandingPage";
import ISettings from "src/lib/types/Settings";

//#region [Other] getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {

    setDefaultCacheHeaders(context);
    // This page should only be used for landing pages for ads, so it should not be indexed
    context.res.setHeader("X-Robots-Tag", "noindex");

    let edi: string | string[] | undefined | null = context.params?.edi;
    const streamingId = context.params?.streamingId;
    if (streamingId) {
        const filminfoResult = await filminfoQuery(FILMINFO_EDI_FROM_STREAMINGID_QUERY, { streamingId });
        edi = filminfoResult?.streamingQuery?.getStreamingContent?.filmwebMovieId;
    }

    if (!edi || !/[A-ZÆØÅa-zæøå]{3}\d{8}/.test(edi as string)) {
        return { notFound: true };
    }
    const isP = isPreview(context);
    const cmsData = await sanityQuery<IQueryResult<IPremiumLandingPage>>(PAGE_QUERY, { edi }, { isPreview: isP });



    const document = cmsData?.published as IPremiumLandingPage | undefined;
    /*
    if (!document) {
        if (streamingId) {
            return {
                redirect: {
                    destination: `/streamingguide/film/${streamingId}`
                }
            };
        }
        return {
            redirect: {
                destination: `/film/${edi}`
            }
        };
    }
    */
    let filmomtaleProps: FilmomtaleProps | undefined;
    const skins = context.query?.skin;
    let testLive = false;
    let skin: string | null = null;
    if (skins) {
        if (Array.isArray(skins)) {
            testLive = skins.includes("testlive");
            skin = skins.includes("nfk") ? "nfk" : null;
        } else {
            testLive = (skins === "testlive");
            skin = skins === "nfk" ? "nfk" : null;
        }
    }
    if (!document?.isLive && !testLive && !isP) {
        const filmomtaleSSR = await filmomtaleGetServerSideProps(context) as any;
        filmomtaleProps = filmomtaleSSR?.props as (FilmomtaleProps | undefined);
    }

    return {
        props: {
            landingpage: document ?? null,
            filmomtaleProps: filmomtaleProps ?? null,
            skin: skin,
            testlive: testLive || isP,
            robots: "noindex"
        }
    }

});
//#endregion

//#region [Props]
type PremiumLandingPageProps = {
    landingpage: IPremiumLandingPage;
    filmomtaleProps?: FilmomtaleProps;
    skin: string | null;
    testlive: boolean;
} & ICommonProps;
//#endregion

//#region [Component]
export default function PremiumLandingPage({ appData, landingpage, filmomtaleProps, skin, testlive }: PremiumLandingPageProps) {


    return <>
        {(landingpage?.isLive || testlive) && <SLandingPage className={`PremiumLandingPage ${landingpage.movie.mainVersionEDI}`}>
            <PremiumLandingPageSeo document={landingpage} />
            <SGlobalStyles {...landingpage} />
            <Hero landingpage={landingpage} />
            <SContentWrapper>
                <Logo landingpage={landingpage} />
                <Ratings landingpage={landingpage} />
                <SContent>
                    {/*<ShortFacts landingpage={landingpage} />*/}
                    <SProgramWrapper>
                        <LocationWrapper canUseGeoLocation={appData.featureFlags.isGeolocationAllowed ?? false} locationSelector={skin === "nfk" ? <NFKLocationSelector /> : undefined}>
                            <SelectedCinemaWrapper>
                                <ProgramDatesWrapper>
                                    <SProgramDisplay
                                        edi={landingpage.movie.mainVersionEDI}
                                        movieTitle={landingpage.movie.title}
                                        premiere={landingpage.movie.premiere}
                                        isKinoklubbMovie={landingpage.movie.isKinoklubb}
                                        genres={landingpage.movie.genres as Genre[]}
                                    >
                                        <ExtraProgramControls landingpage={landingpage} />
                                    </SProgramDisplay>
                                </ProgramDatesWrapper>
                            </SelectedCinemaWrapper>

                        </LocationWrapper>

                    </SProgramWrapper>
                    <SInfoWrapper>
                        <SMainTextContainer>
                            <Ingress ingress={landingpage.movie.ingress} />
                            <MainText mainText={landingpage.movie.bodyText} />
                        </SMainTextContainer>
                        <SFactsContainer>
                            <FilmFacts cmsDocument={landingpage.movie} />
                        </SFactsContainer>
                    </SInfoWrapper>
                    <PromotedVideo landingpage={landingpage} />
                </SContent>
            </SContentWrapper>
        </SLandingPage>}

        {!landingpage?.isLive && filmomtaleProps && <>
            <Filmomtale {...filmomtaleProps} isAdFree hasSponsor={false} adFreeSkin={skin} />
        </>}
    </>;
}

PremiumLandingPage.getLayout = (page: ReactElement<PremiumLandingPageProps>, router: Router, appSettings?: ISettings) => {
    if (page.props.skin === "nfk") {
        return <>
            <NFKHeader />
            {page}
            <NFKFooter />
        </>;
    }
    return <DefaultLayout appSettings={appSettings}>{page}</DefaultLayout>
};

//#endregion

//#region [Other]
const PAGE_QUERY = groq`
{
	"published": *[_type == "${DocType_PremiumLandingPage}" && movie->mainVersionEDI == $edi] {
		...,
		movie-> {
			...,
			${createRichTextQueryFragment("bodyText")},
			${createRichTextQueryFragment("ingress")},
			mainPoster->,
			mainImage->,
			director[]->,
			posters[]->,
			cast[]->,
			norwegianVoiceCast[]->,
			writer[]->,
			photo[]->,
			genres[]->,
			distributor[]->,
			images[]->,
			trailers[]-> {
				...,
				image->
			}
		}
	}[0]
}
`;

const FILMINFO_EDI_FROM_STREAMINGID_QUERY = gql`
query getEDI($streamingId: Int!) {
	streamingQuery {
		getStreamingContent(id: $streamingId) {
			filmwebMovieId
		}
	}
}
`;
//#endregion

//#region [Styles]

const SGlobalStyles = createGlobalStyle<IPremiumLandingPage>`
:root {
	${props => css`
		.PremiumLandingPage.${props.movie.mainVersionEDI} {
			--ADFREE-TITLECOLOR: ${props.titleColor.hex};
			--ADFREE-BGCOLOR: ${props.bgColor.hex};
			--ADFREE-TEXTCOLOR: ${props.textColor.hex};
			--ADFREE-BUTTONCOLOR: ${props.buttonColor.hex};
			--ADFREE-FILMFACTINFOCOLOR: ${props.factsColor?.hex ?? props.textColor.hex};
			--ADFREE-INGRESSCOLOR: ${props.ingressColor?.hex ?? props.textColor.hex};
			--ADFREE-BODYTEXTCOLOR: ${props.bodytextColor?.hex ?? props.textColor.hex};
			--ADFREE-INFOLINECOLOR: ${props.infolineColor?.hex ?? props.textColor.hex};
			--ADFREE-DICECOLOR: ${props.diceColor?.hex ?? props.buttonColor.hex};
			--ADFREE-DICETEXTCOLOR: ${props.diceTextColor?.hex ?? props.buttonColor.hex};
			--ADFREE-ACTIONBUTTONSCOLOR: ${props.actionButtonsColor?.hex ?? props.buttonColor.hex};
			--ADFREE-SELECTORSCOLOR: ${props.selectorsColor?.hex ?? props.buttonColor.hex};
			--ADFREE-SELECTORSTEXTCOLOR: ${props.selectorsTextColor?.hex ?? props.bgColor.hex};
			--ADFREE-SHOWSCOLOR: ${props.showsColor?.hex ?? props.buttonColor.hex};
			--ADFREE-SHOWSTEXTCOLOR: ${props.showsTextColor?.hex ?? props.bgColor.hex};
			--ADFREE-SGPROVIDERBG: ${props.sgProviderBg?.hex ?? theme.palette.sgColor};

			--BACKGROUNDCOLOR_OVERRIDE: var(--ADFREE-BGCOLOR);
			--TEXTCOLOR_OVERRIDE: var(--ADFREE-TEXTCOLOR);

			${props.kkOverrideColor?.hex ? css`
				--kinoklubbcolor: ${props.kkOverrideColor.hex};
			`: ""}
		}
	`}
}
`;

const SLandingPage = styled.article`
	background-color: var(--ADFREE-BGCOLOR);
	position: relative;
	z-index: 0;
	font-family: "Open Sans", sans-serif;
`;

const SContentWrapper = styled.div`
	position: relative;
`;

const SContent = styled.div`
	margin-top: 1vw;
	max-width: 1230px;
	margin: 0 auto;

	@media (max-width: 1230px) {
		padding: 0 10px;
	}
`;

const SProgramWrapper = styled.div`
	--buttoncolor: var(--ADFREE-SELECTORSCOLOR);
	--buttontextcolor: var(--ADFREE-SELECTORSTEXTCOLOR);
	--iconcolor: var(--ADFREE-SELECTORSTEXTCOLOR);

	margin-top: 15px;

	@media (min-width: 550px) {
        /*@media  (orientation: landscape) {*/
        margin-bottom: 35px;
    }
`;

const SProgramDisplay = styled(ProgramDisplay)`
	--BACKGROUNDCOLOR_OVERRIDE: var(--ADFREE-SHOWSCOLOR);
	--TEXTCOLOR_OVERRIDE: var(--ADFREE-SHOWSTEXTCOLOR);
	--ICONCOLOR_OVERRIDE: var(--ADFREE-SHOWSCOLOR);
	--SGBACKGROUNDCOLOR_OVERRIDE: var(--ADFREE-SGPROVIDERBG);
`;

const SInfoWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	@media (min-width: 550px) {
		flex-direction: row;
	}
`;

const SMainTextContainer = styled.div`
	--INGRESSCOLOR-OVERRIDE: var(--ADFREE-INGRESSCOLOR);
	--BODYTEXTCOLOR-OVERRIDE: var(--ADFREE-BODYTEXTCOLOR);

	@media (min-width: 550px) {
		width: 55%;
	}
`;

const SFactsContainer = styled.div`
	--FILMFACTINFOCOLOR-OVERRIDE: var(--ADFREE-FILMFACTINFOCOLOR);
	--TEXTCOLOR-OVERRIDE: var(--ADFREE-TEXTCOLOR);

	@media (min-width: 550px) {
		width: 35%;
	}
`;
//#endregion